<div class="notes-drawer">
  <!-- Header -->
  <div class="drawer-header">
    <div class="header-content">
      <div class="header-text">
        <h2 class="drawer-title">{{ drawerConfig()?.title || 'Notes' }}</h2>
        @if (drawerConfig()?.subtitle) {
        <p class="drawer-subtitle">{{ drawerConfig()?.subtitle }}</p>
        }
      </div>
      <div class="header-actions">
        <button #filterMenuTrigger="matMenuTrigger" class="filter-btn" mat-icon-button [matMenuTriggerFor]="filterMenu">
          <mat-icon>filter_list</mat-icon>
        </button>
        <button class="close-btn" mat-icon-button (click)="closeDrawer()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <!-- Filter Menu -->
  <mat-menu #filterMenu="matMenu" class="filter-menu-panel" xPosition="before" (menuOpened)="onFilterMenuOpened()">
    <div class="filter-menu-content" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
      <div class="filter-section">
        <div class="filter-header">
          <h4 class="filter-title">Sort</h4>
          <button class="close-menu-btn" mat-icon-button (click)="closeFilterMenu()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <button mat-menu-item [class.active]="sortBySignal() === 'date-desc'"
          (click)="onSortChange('date-desc'); $event.stopPropagation()">
          <mat-icon>{{ sortBySignal() === 'date-desc' ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
          <span>Newest</span>
        </button>
        <button mat-menu-item [class.active]="sortBySignal() === 'date-asc'"
          (click)="onSortChange('date-asc'); $event.stopPropagation()">
          <mat-icon>{{ sortBySignal() === 'date-asc' ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
          <span>Oldest</span>
        </button>
      </div>
      @if (selectedTabSignal() === 'agent') {
      <mat-divider />
      <div class="filter-section">
        <h4 class="filter-title">Filter by category</h4>
        @for (category of categories; track category) {
        <button mat-menu-item [class.active]="isCategorySelected(category)"
          (click)="onFilterChange(category); $event.stopPropagation()">
          <mat-icon>{{ isCategorySelected(category) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
          <span>{{ category }}</span>
        </button>
        }
      </div>
      <div class="filter-actions">
        <button class="clear-btn" mat-button type="button" (click)="onClearFilter()">
          Clear
        </button>
        <button color="primary" mat-raised-button type="button" (click)="onApplyFilter()">
          Apply
        </button>
      </div>
      }
    </div>
  </mat-menu>

  <!-- Tabs -->
  <mat-tab-group class="notes-tabs" (selectedIndexChange)="onTabChange($event)">
    <mat-tab label="Order">
      <ng-container
        *ngTemplateOutlet="notesListTemplate; context: { emptyTitle: emptyStateTitle, emptyMessage: emptyStateMessage }" />
    </mat-tab>

    <mat-tab label="Agent">
      <ng-container
        *ngTemplateOutlet="notesListTemplate; context: { emptyTitle: emptyStateTitle, emptyMessage: emptyStateMessage }" />
    </mat-tab>
  </mat-tab-group>

  <!-- Reusable Notes List Template -->
  <ng-template #notesListTemplate let-emptyMessage="emptyMessage" let-emptyTitle="emptyTitle">
    <div class="notes-list-container">
      @if (loadingSpinner().isLoading) {
      <lc-spinner [spinner]="loadingSpinner()" />
      } @else if (filteredNotes().length === 0) {
      <div class="empty-state">
        <mat-icon class="empty-icon" fontSet="material-icons-outlined">note_add</mat-icon>
        <p class="empty-title">{{ emptyTitle }}</p>
        <p class="empty-message">{{ emptyMessage }}</p>
      </div>
      } @else {
      @for (note of filteredNotes(); track note._id) {
      <div class="note-card">
        <!-- Note Header (always visible) -->
        <div class="note-header">
          <div class="note-meta">
            <span class="note-author">{{ note.author }}</span>
            <span class="note-date">
              {{ getRelativeDate(note.audit?.createdAt) }}
              @if (isNoteEdited(note)) {
              <span class="edited-label">(edited)</span>
              }
            </span>
          </div>
          <button class="note-menu-btn" mat-icon-button [matMenuTriggerFor]="noteMenu">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #noteMenu="matMenu" class="note-menu-panel" xPosition="before">
            <button mat-menu-item (click)="onEdit(note)">
              <mat-icon>edit</mat-icon>
              <span>Edit</span>
            </button>
            <button mat-menu-item (click)="onCopy(note)">
              <mat-icon class="copy-icon" svgIcon="content_copy" />
              <span>Copy Note</span>
            </button>
            <button class="delete-option" mat-menu-item (click)="onDelete(note)">
              <mat-icon>delete</mat-icon>
              <span>Delete</span>
            </button>
          </mat-menu>
        </div>

        @if (editingNoteId() === note._id) {
        <!-- Edit Form Inline -->
        <ng-container
          *ngTemplateOutlet="noteFormTemplate; context: { heading: '', saveLabel: 'Save', isInline: true }" />
        } @else {
        <!-- Note Display -->
        <div class="note-text">{{ note.text }}</div>

        @if (selectedTabSignal() === 'agent' && note.category) {
        <div class="note-footer">
          <span class="note-category-badge">
            {{ note.category | titlecase }}
          </span>
        </div>
        }
        }
      </div>
      }
      }
    </div>
  </ng-template>

  <!-- Reusable Note Form Template -->
  <ng-template #noteFormTemplate let-heading="heading" let-isInline="isInline" let-saveLabel="saveLabel">
    <div [class.note-edit-form]="isInline" [class.note-form-container]="!isInline">
      @if (!isInline && heading) {
      <h3 class="form-heading">{{ heading }}</h3>
      }
      @if (isInline && heading) {
      <h4 class="edit-heading">{{ heading }}</h4>
      }

      <form class="note-form" [formGroup]="form">
        @if (selectedTabSignal() === 'agent') {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Note category</mat-label>
          <mat-select formControlName="category" hideSingleSelectionIndicator>
            @for (category of categories; track category) {
            <mat-option [value]="category">{{ category }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        }

        <lc-textarea label="Note" placeholder="Enter your note here..." [formControl]="form.get('text')"
          [idPrefix]="isInline ? 'note-edit' : 'note'" [rows]="3" />

        @if (isSaving()) {
        <div class="form-saving">
          <lc-spinner [spinner]="savingSpinner()" />
        </div>
        } @else {
        <div class="form-actions">
          <button mat-button type="button" (click)="onCancelForm()">
            Cancel
          </button>
          <button color="primary" mat-raised-button type="button" [disabled]="!form.valid || form.pristine"
            (click)="onSave()">
            {{ saveLabel }}
          </button>
        </div>
        }
      </form>
    </div>
  </ng-template>

  <!-- Add Note Section -->
  <div class="add-note-section">
    @if (!showAddNoteForm()) {
    <!-- Add Note Button -->
    <button class="add-note-btn" color="primary" mat-stroked-button (click)="showAddNoteForm.set(true)">
      <mat-icon>add</mat-icon>
      <span>Add note</span>
    </button>
    }

    @if (showAddNoteForm() && !editingNoteId()) {
    <!-- Add Note Form (only for creating new notes) -->
    <ng-container
      *ngTemplateOutlet="noteFormTemplate; context: { heading: 'Add Note', saveLabel: 'Save', isInline: false }" />
    }
  </div>
</div>